import React, { useEffect, useState } from "react";
import { Button, IconButton, Grid, Box, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { Add, List, Close } from "@mui/icons-material";
import { CreateInitialEvaluationDto, GetInitialEvaluationDto } from "../../../api-client";
import moment from "moment";
import InitialEvaluationList from "./InitialEvaluationList";
import InitialEvaluationCard from "./InitialEvaluationCard";
import { useApi } from "../../../api/ApiProvider";
import notificationStore from "../../../stores/Notification/notificationStore";
interface InitialEvaluationsProps {
  patientId: number;
}

const InitialEvaluations: React.FC<InitialEvaluationsProps> = ({ patientId }) => {

  const pId = patientId;
  const { clinicalDocumentation } = useApi();

  const [initialEvaluations, setInitialEvaluations] = useState<GetInitialEvaluationDto[]>([]);
  const [showDummyInitialEvaluation, setShowDummyInitialEvaluation] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const latestInitialEvaluation = initialEvaluations?.[0];

  useEffect(() => {
    if (pId) {
      fetchInitialEvaluations();
    }
  }, [pId]);

  const fetchInitialEvaluations = async () => {
    try {
      // Fetch just the latest (page 1, limit 1) for the main view
      const data = await clinicalDocumentation.getClinicalInitialEvaluation(pId, { params: { limit: 1, page: 1 } } as any);
      setInitialEvaluations(data || []);
    } catch (error: any) {
      console.error("Error fetching initial evaluations:", error);
      notificationStore.showMessage(error?.message, 'error', error);
    }
  };

  const handleAddDummyInitialEvaluation = () => {
    if (!pId) return null;
    setShowDummyInitialEvaluation(true);
  };

  const handleCreateInitialEvaluation = async (initialEvaluation: CreateInitialEvaluationDto) => {
    try {
      const data = await clinicalDocumentation.createClinicalInitialEvaluation(initialEvaluation);
      setInitialEvaluations([data, ...initialEvaluations]);
      setShowDummyInitialEvaluation(false);
    } catch (error: any) {
      console.error(error);
      notificationStore.showMessage(error?.message, 'error', error);
    }
  };

  const handleUpdateInitialEvaluation = async (id: number, initialEvaluation: CreateInitialEvaluationDto) => {
    try {
      const data = await clinicalDocumentation.updateClinicalInitialEvaluation(id, initialEvaluation);
      const index = initialEvaluations.findIndex((ie) => ie.id === id);
      if (index > -1) {
        const updated = [...initialEvaluations];
        updated[index] = data;
        setInitialEvaluations(updated);
      }
      return data;
    } catch (error: any) {
      notificationStore.showMessage(error?.message, 'error', error);
      throw error;
    }
  };

  return (
    <>
      {(showDummyInitialEvaluation || !latestInitialEvaluation) && (
        <Box position="relative" marginBottom={2}>
          <InitialEvaluationCard
            patientId={pId}
            onCreateInitialEvaluation={handleCreateInitialEvaluation}
            onUpdateInitialEvaluation={handleUpdateInitialEvaluation}
            onDeleteInitialEvaluation={() => setShowDummyInitialEvaluation(false)}
          />
        </Box>
      )}

      {latestInitialEvaluation && (
        <Box position="relative">
          {/* Floating Icons */}
          <Grid
            container
            spacing={1}
            sx={{
              position: "absolute",
              top: 0,
              right: -550, 
              zIndex: 10,
              paddingX: 1,
              pointerEvents: "none",
            }}
          >
            <Grid item>
              <IconButton
                color="default"
                onClick={() => setShowDialog(!showDialog)}
                sx={{ pointerEvents: "auto" }}
              >
                <List />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton
                color="default"
                onClick={handleAddDummyInitialEvaluation}
                disabled={showDummyInitialEvaluation}
                sx={{ pointerEvents: "auto" }}
              >
                <Add />
              </IconButton>
            </Grid>
          </Grid>

          {/* Initial Evaluation Card (Now Clickable) */}
          <InitialEvaluationCard
            patientId={pId}
            initialEvaluation={latestInitialEvaluation}
            onCreateInitialEvaluation={handleCreateInitialEvaluation}
            onUpdateInitialEvaluation={handleUpdateInitialEvaluation}
          />
        </Box>
      )}

      <Dialog open={showDialog} onClose={() => setShowDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Initial Evaluations
          <IconButton
            aria-label="close"
            onClick={() => setShowDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <InitialEvaluationList patientId={pId} limit={10} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InitialEvaluations;
